import { useEffect, useState } from "react";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "~/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { z } from "zod";
import { Label } from "~/components/ui/label";

// // // //

/**
 * validateEmail
 * Validates an email address using Zod schema validation
 * @param email The email address to validate
 * @returns boolean indicating if email is valid
 */
function validateEmail(email: string): boolean {
    const emailSchema = z.email();
    const result = emailSchema.safeParse(email);
    return result.success;
}

interface Props {
    email: string | undefined | null;
    isLoading: boolean;
    onSubmit: (code: string, email: string) => void;
}

export function OTPVerification({ email, isLoading, onSubmit }: Props) {
    const [code, setCode] = useState("");

    // Define a state variable to track whether to show the email form
    const [showEmailForm, setShowEmailForm] = useState(false);

    // Pull email from props and set it in state
    const [emailValue, setEmailValue] = useState(email);
    useEffect(() => {
        setEmailValue(email);
    }, [email]);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        // If email is undefined, null or empty string -> show email form
        if (!emailValue) {
            setShowEmailForm(true);
            return;
        }

        // Pull email value from state and trim whitespace
        const trimmedEmail = emailValue.trim();

        // If email is not valid, show email form
        if (!validateEmail(trimmedEmail)) {
            setShowEmailForm(true);
            return;
        }

        onSubmit(code, trimmedEmail);
    }

    // Disable the submit button if loading, or code is not present or not 6 digits
    const disableSubmit = isLoading || !code || code.length !== 6;

    return (
        <form className="gap-4 flex flex-col" onSubmit={handleSubmit}>
            <div className="flex flex-col items-center gap-3">
                <p className="text-center text-xs leading-snug text-secondary-foreground">
                    Code expires after 15 minutes.
                </p>

                <fieldset className="min-w-0 border-0 p-0">
                    <legend className="sr-only">Verification code</legend>
                    <div className="flex justify-center">
                        <InputOTP
                            maxLength={6}
                            value={code}
                            onChange={(value) => setCode(value)}
                            pattern={REGEXP_ONLY_DIGITS}
                            aria-label="Enter 6-digit verification code"
                            disabled={isLoading}
                        >
                            <InputOTPGroup className="gap-0 bg-transparent">
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                                <InputOTPSlot index={4} />
                                <InputOTPSlot index={5} />
                            </InputOTPGroup>
                        </InputOTP>
                    </div>
                </fieldset>

                <p className="text-center text-xs leading-snug text-secondary-foreground">
                    Not receiving the code? First check your spam folder, then
                    ask your IT team to make sure emails from rwa-xyz.com
                    aren&apos;t being blocked.
                </p>
            </div>

            {showEmailForm && (
                <div className="pt-0.5">
                    <Label
                        htmlFor="otp-email-fallback"
                        className="block text-xs font-semibold text-foreground sm:text-sm mb-1"
                    >
                        Email
                    </Label>
                    <Input
                        id="otp-email-fallback"
                        value={emailValue || ""}
                        disabled={isLoading}
                        placeholder="Please re-enter your email"
                        name="email"
                        required
                        type="email"
                        autoComplete="email"
                        onChange={(e) => setEmailValue(e.target.value)}
                    />
                </div>
            )}

            <Button type="submit" variant="primary" disabled={disableSubmit}>
                {isLoading ? "Verifying..." : "Verify Code"}
            </Button>
        </form>
    );
}
